import { Box, Button, Flex, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';

import {
  AddCircleOutline,
  DropdownMultiSelectInput,
  DropdownOption,
  PageEmptyState,
  SearchInput,
  WalletOutline,
} from '@aslanpay/portal-common-lib';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import useGroupWithPotEmployees from 'app/hooks/useGroupsWithPot/useGroupWithPotEmployees';
import PrideEmployeeListModal from 'app/components/Modals/PrideEmployeeListModal';
import CreatePotModal from 'app/components/Modals/CreatePotModal/CreatePotModal';
import { useMemo, useState } from 'react';
import { GroupWithPot, StrictGroupWithPot } from 'app/hooks/useGroupsWithPot/types';
import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';

import TransferPointsModal from 'app/components/Modals/TransferPointsModal/TransferPointsModal';
import { generatePath, useNavigate } from 'react-router-dom';

import { companyPaths } from 'app/constants/routes';

import TopUpPotModal from 'app/components/Modals/TopUpPotModal/TopUpPotModal';
import ArchivePotModal from 'app/components/Modals/ArchivePotModal/ArchivePotModal';

import { PotsProps } from './Pots.types';
import PotCard from './PotCard/PotCard';

function Pots({ companyId }: PotsProps) {
  const navigate = useNavigate();

  const {
    flatGroupsWithPots: allGroups,
    isLoading,
    setSelectedGroup,
    modalProps,
  } = useGroupWithPotEmployees(companyId);

  const flatGroupsWithPots = allGroups.filter((group) => !!group.pot);

  const totalPots = flatGroupsWithPots.length;
  const totalPrides = allGroups.length;

  const {
    isOpen: isOpenCreatePotModal,
    onClose: onCloseCreatePotModal,
    onOpen: onOpenCreatePotModal,
  } = useDisclosure();

  const {
    isOpen: isOpenTransferPointsModal,
    onClose: onCloseTransferPointsModal,
    onOpen: onOpenTransferPointsModal,
  } = useDisclosure();

  const {
    isOpen: isOpenTopUpPotModal,
    onClose: onCloseTopUpPotModal,
    onOpen: onOpenTopUpPotModal,
  } = useDisclosure();

  const {
    isOpen: isOpenArchivePotModal,
    onClose: onCloseArchivePotModal,
    onOpen: onOpenArchivePotModal,
  } = useDisclosure();

  const [selectedPot, setSelectedPot] = useState<StrictGroupWithPot>();

  const [search, setSearch] = useState<string>('');
  const [selectedPrideIds, setSelectedPrideIds] = useState<string[]>([]);

  // Pride dropdown options
  const prideOptions: DropdownOption[] = useMemo(
    () =>
      allGroups.map((group) => ({
        value: group.id,
        text: getGroupDisplayNameWithParents(group),
      })),
    [allGroups]
  );

  const filteredResults: Omit<GroupWithPot, 'children'>[] = useMemo(() => {
    const q = search.toLowerCase();

    return (
      flatGroupsWithPots
        // if no selected prides, show all. otherwise, filter by selected prides
        .filter((m) => selectedPrideIds.length === 0 || selectedPrideIds.includes(m.id))
        // if no search query, show all. otherwise, filter by name
        .filter((m) => !search || m.name.toLowerCase().includes(q))
    );
  }, [flatGroupsWithPots, search, selectedPrideIds]);

  if (isLoading) {
    return <CenterSpinner />;
  }

  return (
    <>
      <Flex
        direction="column"
        gap="28px"
        border="1px solid"
        borderColor="brand.lightGray1"
        p="20px"
        flex="1"
      >
        <Flex justify="space-between" gap="30px" alignItems="center" flex="1">
          <Flex direction="column" gap="8px" align="flex-start">
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              Pots
            </Text>
            <Text as="h6" textStyle="h6Medium" color="brand.darkPurple">
              <Text fontWeight="bold" as="span">
                {totalPots}{' '}
              </Text>
              pot(s) created across{' '}
              <Text fontWeight="bold" as="span">
                {totalPrides}{' '}
              </Text>
              prides
            </Text>
          </Flex>

          <Flex direction="row" gap="15px" align="center">
            <Button
              size="sm"
              leftIcon={<AddCircleOutline boxSize="20px" />}
              onClick={onOpenCreatePotModal}
              isDisabled={totalPots >= totalPrides}
            >
              New pot
            </Button>
          </Flex>
        </Flex>

        <Flex direction="column" gap="25px">
          <Flex justify="space-between" gap="30px" alignItems="center" flex="1">
            <DropdownMultiSelectInput
              options={prideOptions}
              selected={selectedPrideIds}
              onChange={setSelectedPrideIds}
              placeholder="Prides"
              isSelectAllEnabled
              selectAllDisplayLabel="Prides: All"
            />

            <SearchInput
              placeholder="Search"
              searchQuery={search}
              setSearchQuery={setSearch}
              w="330px"
            />
          </Flex>

          {filteredResults.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="30px">
              {filteredResults.map((groupWithPot) => (
                <Box key={groupWithPot.id} w="100%" minH="220px">
                  <PotCard
                    data={groupWithPot}
                    onPeopleCountClick={() => {
                      setSelectedGroup(groupWithPot as StrictGroupWithPot);
                    }}
                    onAddFundsClick={() => {
                      setSelectedPot(groupWithPot as StrictGroupWithPot);
                      onOpenTopUpPotModal();
                    }}
                    onArchiveClick={() => {
                      if (groupWithPot.pot) {
                        setSelectedPot(groupWithPot as StrictGroupWithPot);
                        onOpenArchivePotModal();
                      }
                    }}
                    onReclaimFundsClick={
                      (groupWithPot.pot?.balance || 0) > 0
                        ? () => {
                            if (groupWithPot.pot) {
                              setSelectedPot(groupWithPot as StrictGroupWithPot);
                              onOpenTransferPointsModal();
                            }
                          }
                        : undefined
                    }
                    onManageClick={() => {
                      if (groupWithPot.pot?.id) {
                        navigate(
                          generatePath(companyPaths.pot.path, {
                            companyId,
                            potId: groupWithPot.pot.id,
                          })
                        );
                      }
                    }}
                  />
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <PageEmptyState
              icon={<WalletOutline color="brand.purple" boxSize="40px" />}
              title="No pots found"
              {...(filteredResults.length === 0 && flatGroupsWithPots.length > 0
                ? {
                    description: 'No pots found matching your search.',
                  }
                : {
                    description: 'There are no active pots in your company.',
                  })}
            />
          )}
        </Flex>
      </Flex>
      <PrideEmployeeListModal {...modalProps} />
      <CreatePotModal isOpen={isOpenCreatePotModal} onClose={onCloseCreatePotModal} />
      <TransferPointsModal
        isOpen={isOpenTransferPointsModal}
        onClose={() => {
          onCloseTransferPointsModal();
          setSelectedPot(undefined);
        }}
        originPot={selectedPot?.pot}
      />

      <TopUpPotModal
        isOpen={isOpenTopUpPotModal}
        onClose={() => {
          onCloseTopUpPotModal();
          setSelectedPot(undefined);
        }}
        originPot={selectedPot?.pot}
      />
      {selectedPot && selectedPot.pot && (
        <ArchivePotModal
          isOpen={isOpenArchivePotModal}
          onClose={() => {
            onCloseArchivePotModal();
            setSelectedPot(undefined);
          }}
          originGroupWithPot={selectedPot}
          companyId={companyId}
        />
      )}
    </>
  );
}

export default Pots;
